
import React, { useState } from 'react';
import { format } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Clock, CalendarClock, AlertCircle, UserCheck, UserX } from 'lucide-react';
import AttendanceStatusDialog from './AttendanceStatusDialog';

interface TeacherAttendanceProps {
  id: string;
  name: string;
  avatar?: string;
  attendanceStatus: 'present' | 'absent' | 'late';
  checkInTime?: string;
  reason?: string;
  hasSubstitute?: boolean;
  substituteName?: string;
  classesToday: number;
}

const AttendanceCard = ({ teacher }: { teacher: TeacherAttendanceProps }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-500';
      case 'late':
        return 'bg-yellow-100 text-yellow-800 border-yellow-500';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-500';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };
  
  return (
    <>
      <div className={`border rounded-lg p-4 transition-all hover:shadow-md ${
        teacher.attendanceStatus === 'present' ? 'border-green-200' : 
        teacher.attendanceStatus === 'late' ? 'border-yellow-200' : 
        'border-red-200'
      }`}>
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <Avatar className="h-10 w-10 mr-3">
              <AvatarImage src={teacher.avatar} />
              <AvatarFallback className="bg-primary/10">{getInitials(teacher.name)}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-medium text-base">{teacher.name}</h3>
              <div className="flex items-center text-xs text-muted-foreground">
                <CalendarClock className="h-3 w-3 mr-1" />
                <span>{teacher.classesToday} classes today</span>
              </div>
            </div>
          </div>
          <Badge 
            variant="outline"
            className={`capitalize font-medium ${getStatusColor(teacher.attendanceStatus)}`}
          >
            {teacher.attendanceStatus}
          </Badge>
        </div>
        
        {teacher.checkInTime && (
          <div className="mt-3 flex items-center text-xs text-muted-foreground">
            <Clock className="h-3 w-3 mr-1" />
            <span>Checked in: {format(new Date(teacher.checkInTime), 'h:mm a')}</span>
          </div>
        )}
        
        {teacher.hasSubstitute && teacher.substituteName && (
          <div className="mt-1 text-xs flex items-center text-blue-600">
            <UserCheck className="h-3 w-3 mr-1" />
            <span>Substitute: {teacher.substituteName}</span>
          </div>
        )}
        
        {teacher.reason && (
          <div className="mt-1 text-xs flex items-center text-muted-foreground">
            <AlertCircle className="h-3 w-3 mr-1" />
            <span>{teacher.reason}</span>
          </div>
        )}
        
        <div className="mt-3 flex justify-end">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setIsDialogOpen(true)}
            className="text-xs"
          >
            Update Status
          </Button>
        </div>
      </div>
      
      <AttendanceStatusDialog 
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        teacher={teacher}
      />
    </>
  );
};

export default AttendanceCard;
